import {T, useTranslate} from "@tolgee/react";
import formatPrice from "tools/formats/formatPrice";
import React, {useContext} from "react";
import {FullProduct} from "ts/api/product";
import {CurrencyContext} from "view/context/CurrencyContext";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import FlexBox from "view/components/Layout/FlexBox";
import ImportantIcon from "assets/ImportantIcon";
import {grey} from "@material-ui/core/colors";
import {Divider, Tooltip, useTheme} from "@material-ui/core";
import formatExtraProductName from "tools/formats/formatExtraProductName";
import {useShoppingSummaryStateContext} from "view/components/ShoppingSummary/context";
import {getProductListBorder} from "view/components/ShoppingSummary/helpers";
import clsx from "clsx";
import {useQuickOrderId} from "hooks/show/order";
import useStyles from "./styles";

type ProductProps = {
  quantity: number;
  price: string;
  name: string;
  isApprovalRequired?: boolean;
  mt?: number;
};

function Product({
  quantity,
  price,
  name,
  isApprovalRequired,
  mt = 2,
}: ProductProps) {
  const classes = useStyles();
  const {t} = useTranslate();
  return (
    <FlexBox mt={mt} column>
      <FlexBox alignCenter justifyContent="space-between">
        <FlexBox alignCenter gap={0.5} maxWidth="75%">
          {!!isApprovalRequired && (
            <Tooltip title={t("common.shopping_summary.approval_tooltip")}>
              <Box display="flex">
                <ImportantIcon />
              </Box>
            </Tooltip>
          )}
          <Tooltip title={name}>
            <span className={classes.productName}>{name}</span>
          </Tooltip>
        </FlexBox>
        <span className={classes.contrastText}>{price}</span>
      </FlexBox>
      <span className={classes.countOfItems}>
        <T keyName="common.item_count" params={{count: quantity}} />
      </span>
    </FlexBox>
  );
}

type Products2Props = {
  products: FullProduct[];
  isApprovalRequired?: boolean;
  ownerId: string;
};

export function Products({
  products,
  isApprovalRequired,
  ownerId,
}: Products2Props) {
  const {palette} = useTheme();
  const quickOrderId = useQuickOrderId();
  const currency = useContext(CurrencyContext);
  const {activeOrderGroup} = useShoppingSummaryStateContext();
  const classes = useStyles();
  const border = getProductListBorder(
    activeOrderGroup,
    ownerId,
    palette.primary.main
  );

  const mappedProducts = products.map((product, index, arr) => {
    const cartQuantity = Number(product.cartQuantity);
    return (
      <FlexBox
        className={classes.approvalNotRequired}
        color={grey[500]}
        key={quickOrderId ? index : product.id}
        column
      >
        <Product
          mt={index === 0 ? 0 : 2}
          name={product.name}
          quantity={cartQuantity}
          price={formatPrice(product.priceWithFees * cartQuantity, currency)}
          isApprovalRequired={isApprovalRequired}
        />
        {product.extras?.length
          ? product.extras.map((extra) => (
              <Product
                mt={1}
                key={extra.id}
                isApprovalRequired={isApprovalRequired}
                name={formatExtraProductName(extra.name)}
                quantity={extra.quantity}
                price={formatPrice(
                  extra.priceWithFees * extra.quantity,
                  currency
                )}
              />
            ))
          : null}
        {!!isApprovalRequired && (
          <Box
            mt={2}
            mb={index === arr.length - 1 ? 2 : 0}
            className={classes.approvalRequiredAlert}
          >
            <T keyName="common.shopping_summary.requires_approval" />
          </Box>
        )}
      </FlexBox>
    );
  });

  if (!mappedProducts?.length) {
    return null;
  }

  return (
    <FlexBox column>
      <Box className={classes.productsBlock} style={{border}}>
        {mappedProducts}
      </Box>
      <Box mb={3} mt={isApprovalRequired ? 1 : 3}>
        <Divider />
      </Box>
    </FlexBox>
  );
}

export type SummaryItemProps = {
  title: string;
  value: string;
  bold?: boolean;
  subTitle?: string;
  highlight?: boolean;
};

export function SummaryItem({
  title,
  subTitle,
  value,
  bold,
  highlight = false,
}: SummaryItemProps) {
  const classes = useStyles();

  return (
    <Box display="flex" justifyContent="space-between" marginBottom={2}>
      <Box marginRight={2} className={classes.title}>
        <Typography
          variant="body2"
          className={clsx(
            classes.contrastText,
            highlight && classes.highlightedText
          )}
          style={{fontWeight: bold ? 600 : 400}}
        >
          {title}
        </Typography>
        {subTitle && (
          <Typography
            className={clsx(
              classes.quantity,
              highlight && classes.highlightedText
            )}
          >
            {subTitle}
          </Typography>
        )}
      </Box>
      <Typography
        className={clsx(
          classes.contrastText,
          highlight && classes.highlightedText
        )}
        variant="body2"
        style={{fontWeight: bold ? 600 : 400}}
      >
        {value}
      </Typography>
    </Box>
  );
}
